import { eachLimit } from 'async'

/**
 * STY-730: run uploads with a concurrency limit and ALWAYS release the slot.
 *
 * The old inline queue only called eachLimit's callback on success, so every
 * S3 or DB-registration failure held a slot forever; after five failures the
 * rest of the batch never started and the vendor saw spinners with no error.
 * Here every path (resolve, reject, synchronous throw) settles the file, and
 * the caller gets back which files landed and which failed so it can show a
 * retry.
 */
export function runUploadQueue (files, uploadOne, limit = 5, onSettled = () => {}) {
  const succeeded = []
  const failed = []

  return new Promise((resolve) => {
    eachLimit(files, limit, (file, callback) => {
      let settledOnce = false
      const settle = (entry) => {
        if (settledOnce) return
        settledOnce = true
        if (entry.ok) succeeded.push(entry)
        else failed.push(entry)
        try { onSettled(entry) } catch (e) { /* UI update must not stall the queue */ }
        callback()
      }

      let attempt
      try {
        attempt = Promise.resolve(uploadOne(file))
      } catch (error) {
        attempt = Promise.reject(error)
      }
      attempt.then(
        (result) => settle({ ok: true, file, result }),
        (error) => settle({ ok: false, file, error })
      )
    }, () => resolve({ succeeded, failed }))
  })
}

// Retry only placeholders still present in the page's image list (same object
// identity). A placeholder the vendor deleted while a retry waited for fresh
// credentials must not be uploaded (Codex P2 round 3 on #108).
export function filterStillShown (files, images) {
  const shown = new Set(images || [])
  return (files || []).filter((f) => shown.has(f))
}
